/**
 * RejectionBanner — shown above the editor when an artifact's status is 'rejected'.
 * Displays the last rejection reason and a dismiss control.
 * See specs/phase2/CR-D-artifact-editing-frontend-spec.md §3.9.
 *
 * Dismissing only hides the banner in the parent's local state; it does NOT change the
 * artifact status. The editor remains fully enabled so the PM can revise and resubmit.
 */

import { AlertTriangle, X } from "lucide-react";

import { cn } from "@/lib/utils";
import type { ArtifactRejection } from "@/hooks/useArtifacts";

export interface RejectionBannerProps {
  lastRejection: ArtifactRejection | undefined;
  onDismiss: () => void;
  className?: string;
}

/** Format an ISO timestamp as e.g. "Aug 11, 2026". Falls back to the raw value. */
function formatDate(isoDate: string): string {
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function RejectionBanner({
  lastRejection,
  onDismiss,
  className,
}: RejectionBannerProps): JSX.Element | null {
  if (!lastRejection) return null;

  const { rejected_by, resolved_at, rejection_reason } = lastRejection;

  return (
    <div
      role="alert"
      className={cn(
        // Off-token raw palette replaced with the pairing MessageRow already uses (§9.3c).
        "flex items-start gap-3 rounded-md border border-warning/40 bg-badge-yellow-bg p-3 text-sm text-text-primary",
        className,
      )}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <p className="min-w-0 flex-1">
        Changes requested by <span className="font-medium">{rejected_by}</span>{" "}
        on {formatDate(resolved_at)}:{" "}
        <span className="italic">&ldquo;{rejection_reason}&rdquo;</span>
      </p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss rejection notice"
        className="-mr-1 -mt-1 shrink-0 rounded-md p-1 text-text-secondary transition-colors hover:bg-badge-yellow-bg focus:outline-none focus:ring-2 focus:ring-warning"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

export default RejectionBanner;
